/* ==============================================
   blog-v2.js — multi-blog filtering + search
   - Blog tabs → Section Rendering API on that blog's URL (paginated)
   - "All" tab → restore initial multi-blog render (client-side, instant)
   - Tag tabs → Section Rendering API on tagged URL (single-blog mode)
   - Pagination → Section Rendering API
   - Search → Predictive Search API (searches all articles site-wide)
   J&J 2026-10-18: search results now use the editorial card (same as the grid / Discover mega menu).
   ============================================== */
(function () {
  function initBlogV2(root) {
    var section = (root || document).querySelector('.blog-v2');
    if (!section || section.dataset.blogv2Init) return;
    section.dataset.blogv2Init = '1';

    var sectionId = section.dataset.sectionId;
    var pageUrl = section.dataset.pageUrl || location.pathname;

    var results = section.querySelector('[data-blog-results]');
    var form = section.querySelector('[data-blog-search]');
    var input = form ? form.querySelector('input[name="q"]') : null;
    if (!results) return;

    var originalResults = results.innerHTML;
    var searchTimer;
    var currentRequest = 0;

    function setLoading(on) {
      section.classList.toggle('blog-v2--loading', !!on);
    }

    function setActiveTab(matchFn) {
      section.querySelectorAll('.blog-v2__tab').forEach(function (t) {
        t.classList.toggle('is-active', matchFn(t));
      });
    }

    /* ── Section Rendering API ── */
    function loadUrl(url, push) {
      var token = ++currentRequest;
      setLoading(true);
      var fetchUrl = url + (url.indexOf('?') > -1 ? '&' : '?') + 'section_id=' + encodeURIComponent(sectionId);

      fetch(fetchUrl)
        .then(function (r) { return r.text(); })
        .then(function (html) {
          if (token !== currentRequest) return;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector('[data-blog-results]');
          if (fresh) results.innerHTML = fresh.innerHTML;
          if (push) history.pushState({ type: 'url', url: url }, '', url);
          if (input) input.value = '';
        })
        .catch(function (e) { console.warn('[blog-v2] load failed', e); })
        .finally(function () { if (token === currentRequest) setLoading(false); });
    }

    /* ── Predictive Search API ── */
    function escapeHtml(s) {
      return (s || '').replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    }

    // Editorial card — mirrors snippets/post-card.liquid (variant: 'editorial')
    function cardHTML(a) {
      var img = a.image || (a.featured_image && a.featured_image.url) || '';
      if (img) img += (img.indexOf('?') > -1 ? '&' : '?') + 'width=800';
      var media = img
        ? '<img class="post-card__image" src="' + img + '" alt="' + escapeHtml(a.title) + '" loading="lazy">'
        : '';
      var tag = (a.tags && a.tags.length) ? '<span class="post-card__tag">' + escapeHtml(a.tags[0]) + '</span>' : '';
      var title = escapeHtml(a.title).replace(': ', ':<br>');
      return (
        '<a class="post-card post-card--editorial" href="' + a.url + '">' +
          '<div class="post-card__media">' + media + '</div>' +
          tag +
          '<div class="post-card__overlay">' +
            '<h3 class="post-card__title ff-heading">' + title + '</h3>' +
            '<span class="post-card__arrow" aria-hidden="true">' +
              '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3.5 12h16M13 5.5l6.5 6.5-6.5 6.5" stroke="currentColor" stroke-width="2.4" stroke-linecap="square"/></svg>' +
            '</span>' +
          '</div>' +
        '</a>'
      );
    }

    function runSearch(term, push) {
      var q = (term || '').trim();

      if (!q) {
        results.innerHTML = originalResults;
        if (push) history.pushState({ type: 'base' }, '', pageUrl);
        setActiveTab(function (t) { return t.dataset.blogFilter === 'all' || t.getAttribute('href') === pageUrl; });
        return;
      }

      var token = ++currentRequest;
      setLoading(true);

      fetch('/search/suggest.json?q=' + encodeURIComponent(q) + '&resources[type]=article&resources[limit]=12')
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (token !== currentRequest) return;
          var list = (data.resources && data.resources.results && data.resources.results.articles) || [];
          results.innerHTML = list.length
            ? '<div class="blog-v2__grid">' + list.map(cardHTML).join('') + '</div>'
            : '<p class="blog-v2__empty">No posts found.</p>';
          if (push) {
            history.pushState({ type: 'search', q: q }, '', pageUrl + '?blog-search=' + encodeURIComponent(q));
          }
          section.querySelectorAll('.blog-v2__tab').forEach(function (t) { t.classList.remove('is-active'); });
        })
        .catch(function (e) { console.warn('[blog-v2] search failed', e); })
        .finally(function () { if (token === currentRequest) setLoading(false); });
    }

    /* ── Events ── */

    // Tab clicks — handles both <button data-blog-filter> (multi-blog) and <a> (tag filter)
    section.addEventListener('click', function (e) {
      var tab = e.target.closest('.blog-v2__tab');
      if (!tab) return;
      e.preventDefault();

      if (tab.dataset.blogFilter !== undefined) {
        /* Multi-blog button tab */
        var filter = tab.dataset.blogFilter;
        setActiveTab(function (t) { return t === tab; });
        if (input) input.value = '';

        if (filter === 'all') {
          ++currentRequest;
          setLoading(false);
          results.innerHTML = originalResults;
          history.pushState({ type: 'base' }, '', pageUrl);
          return;
        }

        /* Specific blog → push ?blog=HANDLE then Section Rendering API */
        history.pushState({ type: 'blog', handle: filter }, '', pageUrl + '?blog=' + encodeURIComponent(filter));
        var bUrl = tab.dataset.blogUrl;
        if (bUrl) loadUrl(bUrl, false);
        return;
      }

      /* Single-blog anchor tag tab */
      loadUrl(tab.getAttribute('href'), true);
    });

    // Pagination links inside results
    results.addEventListener('click', function (e) {
      var a = e.target.closest('.blog-v2__pagination a');
      if (!a) return;
      e.preventDefault();
      loadUrl(a.getAttribute('href'), true);
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    // Search form
    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        clearTimeout(searchTimer);
        runSearch(input.value, true);
      });
      input.addEventListener('input', function () {
        clearTimeout(searchTimer);
        searchTimer = setTimeout(function () { runSearch(input.value, true); }, 350);
      });
    }

    function loadBlogByHandle(handle) {
      var matchTab = section.querySelector('[data-blog-filter="' + handle + '"]');
      if (!matchTab) return;
      setActiveTab(function (t) { return t === matchTab; });
      if (input) input.value = '';
      var bUrl = matchTab.dataset.blogUrl;
      if (bUrl) loadUrl(bUrl, false);
    }

    // Back / forward
    window.addEventListener('popstate', function () {
      var params = new URLSearchParams(location.search);
      var s = params.get('blog-search');
      var b = params.get('blog');
      if (s) {
        if (input) input.value = s;
        runSearch(s, false);
      } else if (b) {
        loadBlogByHandle(b);
      } else {
        results.innerHTML = originalResults;
        if (input) input.value = '';
        setActiveTab(function (t) { return t.dataset.blogFilter === 'all' || t.getAttribute('href') === pageUrl; });
      }
    });

    /* ── On load: restore state from URL ── */
    var initParams = new URLSearchParams(location.search);
    var initSearch = initParams.get('blog-search');
    var initBlog = initParams.get('blog');
    if (initSearch) {
      if (input) input.value = initSearch;
      runSearch(initSearch, false);
    } else if (initBlog) {
      loadBlogByHandle(initBlog);
    }
  }

  initBlogV2();
  document.addEventListener('shopify:section:load', function (e) { initBlogV2(e.target); });
})();
